import { VideoMeetingChat } from "./VideoMeetingChat";
import { useChatVisibility } from "@platform/hooks/useChatVisibility";
import { useTalkJSData } from "@platform/hooks/useChat";
import { useMeetingChatConversationId } from "@platform/hooks/useMeetingChatConversationId";
import { useFetchUserCircles } from "@core/features/circle";
import { usePlatformStore, useChatSessionRetry } from "@platform/store";
import { useTracking } from "@core/features/tracking";
import { parseBrowserAttribution } from "@core/features/videoMeeting/utils/getTrackingMetadata";

export function VideoMeetingChatContainer() {
  const user = usePlatformStore((s) => s.user);
  const talkjsData = useTalkJSData();
  const conversationId = useMeetingChatConversationId();
  const { data: circlesData } = useFetchUserCircles({ user });
  const userCircles = circlesData?.circles ?? null;
  const { shouldShowChat, isLoading } = useChatVisibility({ userCircles });
  const { key: sessionRetryKey, bump: bumpSessionRetry } =
    useChatSessionRetry();
  const { trackEvent } = useTracking();

  if (isLoading || !shouldShowChat) {
    return null;
  }

  // REQUIRED per docs/chat-stuck-spinner-fix-plan.md §3.7. Both events
  // MUST include browser attribution. surface is 'meeting' here.
  const handleSessionRetry = (ctx: { conversationId: string | null }) => {
    const { browser_name, browser_version } = parseBrowserAttribution();
    trackEvent({
      name: "chat.chatbox.retry_clicked",
      params: {
        surface: "meeting",
        conversation_id: ctx.conversationId,
        browser_name,
        browser_version,
      },
    });
    bumpSessionRetry();
  };

  const handleChatboxStuckTracked = (ctx: {
    conversationId: string | null;
  }) => {
    const { browser_name, browser_version } = parseBrowserAttribution();
    trackEvent({
      name: "chat.chatbox.stuck_detector.tripped",
      params: {
        surface: "meeting",
        route:
          typeof window !== "undefined" ? window.location.pathname : "<ssr>",
        conversation_id: ctx.conversationId,
        browser_name,
        browser_version,
      },
    });
  };

  return (
    <VideoMeetingChat
      talkjsData={talkjsData}
      user={user}
      conversationId={conversationId}
      sessionRetryKey={sessionRetryKey}
      onSessionRetry={handleSessionRetry}
      onChatboxStuckTracked={handleChatboxStuckTracked}
    />
  );
}

export default VideoMeetingChatContainer;
